import { ChangeDetectorRef, Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { Workflow, WorkflowStatus, WorkflowType } from '../../../../core/models/models';
import { WorkflowService } from '../../../../core/services/workflow.service';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'app-workflow-list',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatPaginatorModule],
  templateUrl: './workflow-list.component.html',
  styleUrl: './workflow-list.component.scss'
})
export class WorkflowListComponent implements OnInit {
  workflows: Workflow[] = [];
  errorMessage = '';
  successMessage = '';
  workflowStatusOptions = Object.values(WorkflowStatus);
  workflowTypeOptions: Array<WorkflowType | 'ALL'> = ['ALL', ...Object.values(WorkflowType)];
  statusFilter: WorkflowStatus | 'ALL' = 'ALL';
  typeFilter: WorkflowType | 'ALL' = 'ALL';
  searchQuery = '';
  page = signal(0);
  size = signal(10);
  totalElements = signal(0);
  sortField = 'name';
  sortDirection = 'asc';

  private searchSubject = new Subject<string>();

  constructor(
    private workflowService: WorkflowService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadWorkflows();
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(query => {
      this.searchQuery = query;
      this.page.set(0);
      this.loadWorkflows();
    });
  }

  loadWorkflows(): void {
    this.workflowService.getWorkflows(
      this.searchQuery,
      this.page(),
      this.size(),
      this.statusFilter,
      `${this.sortField},${this.sortDirection}`,
      this.typeFilter
    ).subscribe({
      next: res => {
        this.workflows = res.content;
        this.totalElements.set(res.totalElements);
        this.cdr.detectChanges();
      },
      error: () => {
        this.errorMessage = 'Erreur lors du chargement des workflows';
        this.cdr.detectChanges();
      }
    });
  }

  openCreatePage(): void {
    this.router.navigate(['/settings/workflows/new'], { queryParams: { type: this.typeFilter } });
  }

  openEditPage(workflow: Workflow): void {
    if (!workflow.id) {
      this.errorMessage = 'Workflow invalide';
      this.cdr.detectChanges();
      return;
    }
    this.router.navigate(['/settings/workflows/edit', workflow.id]);
  }

  createVersion(workflow: Workflow): void {
    if (!workflow.id) {
      return;
    }
    this.workflowService.createWorkflowVersion(workflow.id).subscribe({
      next: () => {
        this.successMessage = 'Version du workflow creee avec succes.';
        this.page.set(0);
        this.loadWorkflows();
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.errorMessage = err?.error?.message || err?.error || 'Erreur lors de la duplication';
        this.cdr.detectChanges();
      }
    });
  }

  onSearchChange(value: string): void {
    this.searchSubject.next(value);
  }

  onStatusFilterChange(value: WorkflowStatus | 'ALL'): void {
    this.statusFilter = value;
    this.page.set(0);
    this.loadWorkflows();
  }

  onTypeFilterChange(value: WorkflowType | 'ALL'): void {
    this.typeFilter = value;
    this.page.set(0);
    this.loadWorkflows();
  }

  onPageChange(event: PageEvent): void {
    this.page.set(event.pageIndex);
    this.size.set(event.pageSize);
    this.loadWorkflows();
  }
}
